import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router";
import Carousel from "react-bootstrap/Carousel";
import { Container } from "react-bootstrap";
import { useOpen } from "../../hooks/Open";
import { useUserData } from "../../hooks/UserContext";
import notify from "../../components/UI/toast";
import { LoaderSmall } from "../../components/Loader";
import { clientesApi, configApi } from "../OrderResumePag/_services/api";
import Categories from "../../components/FeaturedCategories";
import Brands from "../../components/FeaturedBrands";
import ProductsCarousel from "../../components/FeaturedProducts";
import SeeAllBrands from "../../components/BurgerMenuContents/MenuContainer/Brands";
import SeeAllCategory from "../../components/BurgerMenuContents/MenuContainer/Categories";
import {
  Banner,
  FeaturedCategoriesBrands,
  Category,
  Brand,
  CotainerProduct,
} from "./styles";
import { Link } from "react-router-dom";
import EmailRequiredModal from "components/GlobalModals/EmailRequiredModal";
import { useBannerTracking } from "../../hooks/useBannerTracking";
import { trackViewItemListMarketplace } from "../../utils/gtm";
import { trackClick } from "../../utils/gtm";
import { tagIdFromLegacySuffix } from "../../utils/marketplaceTagIds";
import { useScreenTracking } from "../../hooks/useScreenTracking";

export default function Home() {
  const navigate = useNavigate();
  const jwt = useUserData();
  const channel = process.env.REACT_APP_DEFAULT_CHANNEL;
  const { categorySeeAll, brandsSeeAll, forceCloseAllMenu } = useOpen();
  const firstBannerSent = useRef(false);
  const screenName = useScreenTracking();

  const [loading, setLoading] = useState(true);
  const [brands, setBrands] = useState([]);
  const [banners, setBanners] = useState([]);
  const [sections, setSections] = useState([]);
  const [wishlist, setWishlist] = useState([]);
  const [categories, setCategories] = useState([]);
  const [showEmailRequired, setShowEmailRequired] = useState(false);

  const { handleBannerView, handleBannerClick, userInteraction, setUserInteraction } = useBannerTracking(
    banners,
    channel,
    firstBannerSent
  );

  const mapSectionWithWishlist = useCallback((section, wishlistData) => {
    if (!section.items || !Array.isArray(section.items)) {
      return section;
    }

    return {
      ...section,
      items: section.items.map((product) => ({
        ...product,
        wishlist: wishlistData?.some(
          (item) => item.product_uuid === product.id,
        ),
      })),
    };
  }, []);

  const onClickLocation = useCallback(
    (location, item_type, item_uuid) => {
      if (item_type === "marca") {
        navigate(`/loja/brands/${item_uuid}`);
      } else if (item_type === "categoria") {
        navigate(`/loja/category/${item_uuid}`);
      } else if (item_type === "produto") {
        navigate(`/loja/product/${item_uuid}`);
      } else {
        navigate(location);
      }
    },
    [navigate],
  );

  const getHome = useCallback(async () => {
    try {
      const response = await configApi.get("home/channel?clean=true");
      return response.data;
    } catch (err) {
      console.error("Erro em getHome:", err);
      notify("Erro ao carregar home", "error");
      throw err;
    }
  }, []);

  const getWishlist = useCallback(async () => {
    const userCpf = jwt.userData?.data?.cpf;

    if (!userCpf) {
      return [];
    }

    try {
      const response = await clientesApi.get(`/customer/wishlist`);
      return response.data.data;
    } catch (error) {
      console.error("Erro ao buscar wishlist:", error);
      return [];
    }
  }, [jwt.userData?.data?.cpf]);

  useEffect(() => {
    if (!jwt.userData?.data) return;

    if (jwt.isPJ && !jwt.userData.data?.email) {
      setShowEmailRequired(true);
    }

    const fetchHome = async () => {
      setLoading(true);
      try {
        const [wishlistData, homeData] = await Promise.all([
          getWishlist(),
          getHome(),
        ]);

        setWishlist(wishlistData);

        const {
          brands: homeBrands,
          banners: homeBanners,
          categories: homeCategories,
          extension,
        } = homeData || {};

        homeBrands && setBrands(homeBrands);

        if (homeBanners && Array.isArray(homeBanners)) {
          const sortedBanners = [...homeBanners].sort((a, b) => {
            const orderingA = Number(a.ordering) || 999999;
            const orderingB = Number(b.ordering) || 999999;

            return orderingA - orderingB;
          });
          setBanners(sortedBanners);
        }

        homeCategories && setCategories(homeCategories);

        if (extension && Array.isArray(extension)) {
          const sectionsWithWishlist = extension.map((section) =>
            mapSectionWithWishlist(section, wishlistData),
          );
          setSections(sectionsWithWishlist);

          sectionsWithWishlist.forEach((section) => {
            if (section.items?.length > 0) {
              trackViewItemListMarketplace({
                itemListName: section.label,
                items: section.items.map((product, index) => ({
                  itemId: product.id || "",
                  itemName: product.label || "",
                  discount: product.price?.BRL?.offer !== product.price?.BRL?.price
                    ? Math.max(0, parseFloat(product.price?.BRL?.price || 0) - parseFloat(product.price?.BRL?.offer || 0))
                    : 0,
                  index,
                  itemBrand: product.brand?.name || "",
                  itemCategory: "",
                  price: parseFloat(product.price?.BRL?.offer || 0),
                  quantity: 1,
                })),
              });
            }
          });
        }
      } catch (err) {
        console.error("Erro no fetchHome:", err);
        notify("Erro ao tentar carregar home", "error");
      } finally {
        setLoading(false);
      }
    };

    fetchHome();
    localStorage.removeItem("mainAddress");
  }, [jwt.userData?.data, getWishlist, getHome, mapSectionWithWishlist]);

  const clickCategory = useCallback(() => {
    trackClick({
      title: "ver-todas",
      contentType: "categorias-em-destaque",
      screenName,
      tagId: tagIdFromLegacySuffix("01"),
    });
    navigate(`/loja/categories/`);
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }, [navigate, screenName]);

  const clickBrands = useCallback(() => {
    trackClick({
      title: "ver-todas",
      contentType: "marcas-em-destaque",
      screenName,
      tagId: tagIdFromLegacySuffix("01"),
    });
    navigate(`/loja/brands/`);
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }, [navigate, screenName]);

  const parseTravelBanner = (uri) => {
    try {
      const decoded = uri.replace(/&#34;/g, '"').replace(/&amp;/g, '&');
      const parsed = JSON.parse(decoded);
      if (parsed && (parsed.bannerType === "hotel" || parsed.bannerType === "passagem")) {
        return parsed;
      }
    } catch {
      // not JSON
    }
    return null;
  };

  if (loading) {
    return <LoaderSmall />;
  }

  return (
    <>
      <EmailRequiredModal
        show={showEmailRequired}
        onClose={() => setShowEmailRequired(false)}
        onRegisterEmail={() => {
          setShowEmailRequired(false);
          navigate("/loja/pj/myaccount/");
        }}
        onContinue={() => setShowEmailRequired(false)}
      />
      {categorySeeAll && <SeeAllCategory />}
      {brandsSeeAll && <SeeAllBrands />}
      <div className="wl-page">
        <Banner>
          <Carousel 
            variant="dark" 
            onSlide={handleBannerView}
            onMouseDown={() => setUserInteraction(true)}
            onTouchStart={() => setUserInteraction(true)}
          >
            {banners?.map((banner, bannerIndex) => {
              const { s3_file, uri, item_type, id } = banner;
              const travelData = uri ? parseTravelBanner(uri) : null;
              const hasLink = !!uri && !travelData;

              return (
                <Carousel.Item key={id || `carousel-${s3_file}`}>
                  {travelData ? (
                    <img
                      className="d-block w-100"
                      src={s3_file}
                      alt={`Banner promocional ${item_type || ""}`}
                      style={{ cursor: "pointer" }}
                      onClick={() => {
                        handleBannerClick(bannerIndex);
                        sessionStorage.setItem(
                          "pending_banner_redirect",
                          JSON.stringify(travelData),
                        );
                        navigate(
                          travelData.bannerType === "hotel"
                            ? "/loja/hoteis"
                            : "/loja/passagens-aereas",
                        );
                      }}
                    />
                  ) : hasLink ? (
                    <Link to={uri} onClick={() => handleBannerClick(bannerIndex)}>
                      <img
                        className="d-block w-100"
                        src={s3_file}
                        alt={`Banner promocional ${item_type || ""}`}
                      />
                    </Link>
                  ) : (
                    <img
                      className="d-block w-100"
                      src={s3_file}
                      alt="Banner promocional"
                    />
                  )}
                </Carousel.Item>
              );
            })}
          </Carousel>
        </Banner>

        <Container>
          <FeaturedCategoriesBrands>
            <span>Categorias em destaque</span>
            <button type="button" onClick={clickCategory}>
              VER TODAS
            </button>
          </FeaturedCategoriesBrands>

          <Category>
            <Categories categoriesData={categories} />
          </Category>

          <FeaturedCategoriesBrands>
            <span>Marcas em destaque</span>
            <button type="button" onClick={clickBrands}>
              VER TODAS
            </button>
          </FeaturedCategoriesBrands>

          <Brand>
            <Brands brandsData={brands} />
          </Brand>

          <CotainerProduct>
            <ProductsCarousel sectionsData={sections} />
          </CotainerProduct>
        </Container>
      </div>
    </>
  );
}
